import { Job } from '../types/Job';
import { supabase } from '../lib/supabase';

interface DbJob {
  id: string;
  slug?: string;
  title: string;
  company: string;
  location: string;
  country: string;
  type: string;
  salary?: string;
  description: string;
  requirements: string[];
  benefits?: string[];
  date_posted: string;
  contact_email: string;
  is_active: boolean;
  header_image?: string;
}

const dbToJob = (dbJob: DbJob): Job => ({
  id: dbJob.id,
  slug: dbJob.slug,
  title: dbJob.title,
  company: dbJob.company,
  location: dbJob.location,
  country: dbJob.country,
  type: dbJob.type as Job['type'],
  salary: dbJob.salary,
  description: dbJob.description,
  requirements: dbJob.requirements,
  benefits: dbJob.benefits,
  datePosted: dbJob.date_posted,
  contactEmail: dbJob.contact_email,
  isActive: dbJob.is_active,
  headerImage: dbJob.header_image,
});

const jobToDb = (job: Job): DbJob => ({
  id: job.id,
  slug: job.slug,
  title: job.title,
  company: job.company,
  location: job.location,
  country: job.country,
  type: job.type,
  salary: job.salary,
  description: job.description,
  requirements: job.requirements,
  benefits: job.benefits,
  date_posted: job.datePosted,
  contact_email: job.contactEmail,
  is_active: job.isActive,
  header_image: job.headerImage,
});

export const generateSlug = (title: string, location: string, country: string): string => {
  const text = `${title} ${location} ${country}`;
  return text
    .toLowerCase()
    .replace(/\/[ao]/g, '') // Handle Spanish gender suffixes like Camarero/a
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (í→i, é→e, etc.)
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const ensureUniqueSlug = async (baseSlug: string, excludeId?: string): Promise<string> => {
  let slug = baseSlug;
  let counter = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data } = await supabase
      .from('jobs')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();

    if (!data || (excludeId && data.id === excludeId)) {
      return slug;
    }
    counter++;
    slug = `${baseSlug}-${counter}`;
  }
};

export const getJobs = async (): Promise<Job[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .order('date_posted', { ascending: false });

  if (error) {
    console.error('Error fetching jobs:', error.message, error);
    return [];
  }

  return (data || []).map(dbToJob);
};

export const addJob = async (job: Job): Promise<void> => {
  console.log('Attempting to add job:', job);
  const baseSlug = generateSlug(job.title, job.location, job.country);
  job.slug = await ensureUniqueSlug(baseSlug);
  const dbJob = jobToDb(job);
  const { data, error } = await supabase
    .from('jobs')
    .insert([dbJob])
    .select();

  if (error) {
    console.error('Error adding job:', error.message, error);
    throw error;
  }

  console.log('Job added successfully:', data);
};

export const updateJob = async (id: string, updatedJob: Partial<Job>): Promise<void> => {
  const dbUpdates: Partial<DbJob> = {};

  if (updatedJob.title !== undefined) dbUpdates.title = updatedJob.title;
  if (updatedJob.company !== undefined) dbUpdates.company = updatedJob.company;
  if (updatedJob.location !== undefined) dbUpdates.location = updatedJob.location;
  if (updatedJob.country !== undefined) dbUpdates.country = updatedJob.country;
  if (updatedJob.type !== undefined) dbUpdates.type = updatedJob.type;
  if (updatedJob.salary !== undefined) dbUpdates.salary = updatedJob.salary;
  if (updatedJob.description !== undefined) dbUpdates.description = updatedJob.description;
  if (updatedJob.requirements !== undefined) dbUpdates.requirements = updatedJob.requirements;
  if (updatedJob.benefits !== undefined) dbUpdates.benefits = updatedJob.benefits;
  if (updatedJob.datePosted !== undefined) dbUpdates.date_posted = updatedJob.datePosted;
  if (updatedJob.contactEmail !== undefined) dbUpdates.contact_email = updatedJob.contactEmail;
  if (updatedJob.isActive !== undefined) dbUpdates.is_active = updatedJob.isActive;
  if (updatedJob.headerImage !== undefined) dbUpdates.header_image = updatedJob.headerImage;

  // Regenerate slug if title, location, or country changed
  if (updatedJob.title !== undefined || updatedJob.location !== undefined || updatedJob.country !== undefined) {
    const existing = await getJobById(id);
    if (existing) {
      const title = updatedJob.title ?? existing.title;
      const location = updatedJob.location ?? existing.location;
      const country = updatedJob.country ?? existing.country;
      const baseSlug = generateSlug(title, location, country);
      dbUpdates.slug = await ensureUniqueSlug(baseSlug, id);
    }
  }

  const { error } = await supabase
    .from('jobs')
    .update(dbUpdates)
    .eq('id', id);

  if (error) {
    console.error('Error updating job:', error);
    throw error;
  }
};

export const deleteJob = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('jobs')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting job:', error);
    throw error;
  }
};

export const getJobById = async (id: string): Promise<Job | null> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching job:', error);
    return null;
  }

  return data ? dbToJob(data) : null;
};

export const getJobBySlug = async (slugOrId: string): Promise<Job | null> => {
  // Try slug first
  const { data: slugData } = await supabase
    .from('jobs')
    .select('*')
    .eq('slug', slugOrId)
    .maybeSingle();

  if (slugData) return dbToJob(slugData);

  // Fall back to ID lookup for backward compatibility
  const { data: idData } = await supabase
    .from('jobs')
    .select('*')
    .eq('id', slugOrId)
    .maybeSingle();

  return idData ? dbToJob(idData) : null;
};

export const getUniqueCountries = async (): Promise<string[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('country');

  if (error) {
    console.error('Error fetching countries:', error);
    return [];
  }

  const countrySet = new Set<string>();
  (data || []).forEach(job => {
    if (job.country) countrySet.add(job.country);
  });
  return Array.from(countrySet).sort();
};

// Page Content CMS functions
export interface PageContent {
  id: string;
  page_slug: string;
  section_key: string;
  content_type: string;
  content: string;
  sort_order: number;
  is_active: boolean;
}

export const getPageContent = async (pageSlug: string): Promise<Record<string, string>> => {
  const { data, error } = await supabase
    .from('page_content')
    .select('*')
    .eq('page_slug', pageSlug)
    .eq('is_active', true)
    .order('sort_order');

  if (error) {
    console.error('Error fetching page content:', error);
    return {};
  }

  const content: Record<string, string> = {};
  (data || []).forEach((item: PageContent) => {
    content[item.section_key] = item.content;
  });
  return content;
};

export const getAllPageContent = async (pageSlug: string): Promise<PageContent[]> => {
  const { data, error } = await supabase
    .from('page_content')
    .select('*')
    .eq('page_slug', pageSlug)
    .order('sort_order');

  if (error) {
    console.error('Error fetching page content:', error);
    return [];
  }

  return data || [];
};

export const updatePageContent = async (id: string, content: string): Promise<boolean> => {
  const { error } = await supabase
    .from('page_content')
    .update({ content })
    .eq('id', id);

  if (error) {
    console.error('Error updating page content:', error);
    return false;
  }

  return true;
};

// Page metadata functions
export interface PageMeta {
  id: string;
  slug: string;
  title: string;
  is_published: boolean;
}

export const getPageMeta = async (slug: string): Promise<PageMeta | null> => {
  const { data, error } = await supabase
    .from('pages')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    console.error('Error fetching page meta:', error);
    return null;
  }

  return data;
};

export const getPublishedPages = async (): Promise<PageMeta[]> => {
  const { data, error } = await supabase
    .from('pages')
    .select('*')
    .eq('is_published', true);

  if (error) {
    console.error('Error fetching published pages:', error);
    return [];
  }

  return data || [];
};

export const togglePagePublish = async (slug: string, isPublished: boolean): Promise<boolean> => {
  const { error } = await supabase
    .from('pages')
    .update({ is_published: isPublished })
    .eq('slug', slug);

  if (error) {
    console.error('Error toggling page publish:', error);
    return false;
  }

  return true;
};